import FavoriteBorderRounded from "@mui/icons-material/FavoriteBorderRounded";
import FavoriteRounded from "@mui/icons-material/FavoriteRounded";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { useId } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { userApi } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import { visuallyHidden } from "../../../common/visuallyHidden";

const FAVOURITES_KEY = ["user", "favourite-facilities"] as const;

/**
 * Save a place as one you play at. Favourites appear on your profile's "Plays
 * at" only if you choose to show them. Signed-in players only.
 */
export function FavouriteFacilityButton({ facilityId, facilityName }: { facilityId: string; facilityName: string }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const favourites = useQuery({
    queryKey: [...FAVOURITES_KEY, user?.uid ?? null],
    queryFn: () => userApi.listFavourites(),
    enabled: Boolean(user),
  });
  const saved = favourites.data?.includes(facilityId) ?? false;
  const errorId = useId();
  const toggle = useMutation({
    mutationFn: () => (saved ? userApi.removeFavourite(facilityId) : userApi.addFavourite(facilityId)),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: FAVOURITES_KEY });
      // The player's own profile lists these under "Plays at".
      void queryClient.invalidateQueries({ queryKey: ["compete", "profile-summary"] });
    },
  });
  if (!user || !favourites.isSuccess) return null;
  const label = saved ? `Remove ${facilityName} from places you play` : `Save ${facilityName} as a place you play`;
  const button = (
    <Tooltip title={toggle.isError ? "Couldn't update. Try again." : saved ? "Saved" : "Save place"}>
      <IconButton
        aria-label={label}
        aria-pressed={saved}
        disabled={toggle.isPending}
        onClick={() => toggle.mutate()}
        aria-describedby={toggle.isError ? errorId : undefined}
        sx={{ border: 1, borderColor: toggle.isError ? "error.main" : "divider", borderRadius: 2.5, color: saved ? "primary.main" : undefined }}
      >
        {saved ? <FavoriteRounded fontSize="small" /> : <FavoriteBorderRounded fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
  return toggle.isError ? (
    <>
      {button}
      <Box component="span" id={errorId} role="alert" sx={visuallyHidden}>
        Couldn&apos;t update your saved places. Try again.
      </Box>
    </>
  ) : (
    button
  );
}
